import { useQuery } from "@apollo/client";
import { TextFieldProps } from "@mui/material";
import { gql } from "generated-graphql";
import { FacilityPickerFragment } from "generated-graphql/graphql";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { ControllerFieldState } from "react-hook-form";

import AsyncAutocomplete from "./Forms/AsyncAutocomplete";

gql(`
  fragment FacilityPicker on Facility {
    id
    name
    streetAddress1
    state
  }
`);

const FACILITIES_QUERY = gql(`
  query FacilitiesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
      }
      count
    }
  }
`);

const WASTE_ENABLED_FACILITIES_QUERY = gql(`
  query WasteEnabledFacilitiesPicker($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    facilities: wasteEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        ...FacilityPicker
      }
      count
    }
  }
`);

type FacilityQueryVariant = "all" | "waste";

/** Value can be a full fragment, a string ID, an array containing a single ID, or null. */
type FacilityPickerValue = FacilityPickerFragment | string | string[] | null | undefined;

interface FacilityPickerProps extends ControllerFieldState {
  onChange: (facility: FacilityPickerFragment | null) => void;
  defaultSearchTerm?: string;
  disabledFacilitiesById?: string[];
  disabledOptionLabel?: string;
  label?: string;
  value?: FacilityPickerValue;
  lazyLoad?: boolean;
  disabled?: boolean;
  textFieldProps?: TextFieldProps;
  showErrors?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  facilityQueryVariant?: FacilityQueryVariant;
}

function isFullFacilityFragment(v: unknown): v is FacilityPickerFragment {
  return v != null && typeof v === "object" && "id" in v && "name" in v;
}

/** Extract a facility ID from the flexible value type, or null if no ID. */
function extractId(value: FacilityPickerValue): string | null {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0] ?? null;
  if (typeof value === "object" && "id" in value) return value.id;
  return null;
}

export const FacilityPicker = forwardRef<HTMLDivElement, FacilityPickerProps>(
  function FacilityPicker(
    {
      onChange,
      defaultSearchTerm = "",
      disabledFacilitiesById = [],
      disabledOptionLabel = "",
      label,
      value,
      error,
      lazyLoad = true,
      disabled,
      textFieldProps,
      showErrors = true,
      onOpen,
      onClose,
      facilityQueryVariant = "all",
    },
    ref
  ) {
    const itemCache = useRef<Map<string, FacilityPickerFragment>>(new Map());
    const [resolvedValue, setResolvedValue] = useState<FacilityPickerFragment | null>(null);

    // If the value is already a full object, cache it and use it directly.
    // Otherwise, determine which ID needs hydration.
    const idToHydrate = useMemo(() => {
      if (!value) return null;
      if (isFullFacilityFragment(value)) {
        itemCache.current.set(value.id, value);
        return null;
      }
      const id = extractId(value);
      if (id && itemCache.current.has(id)) return null;
      return id;
    }, [value]);

    const query =
      facilityQueryVariant === "waste" ? WASTE_ENABLED_FACILITIES_QUERY : FACILITIES_QUERY;

    const { data: hydrationData, loading: hydrationLoading } = useQuery(query, {
      variables: {
        search: idToHydrate ? `ids:${idToHydrate}` : "",
        pageSize: 1,
        page: 0,
      },
      skip: !idToHydrate,
    });

    // Cache hydrated results.
    useEffect(() => {
      if (hydrationData) {
        const items = hydrationData.facilities?.items ?? [];
        items.forEach((item: FacilityPickerFragment) => {
          itemCache.current.set(item.id, item);
        });
      }
    }, [hydrationData]);

    // Resolve the current value from the cache whenever value or hydration changes.
    useEffect(() => {
      if (!value) {
        setResolvedValue(null);
        return;
      }
      if (isFullFacilityFragment(value)) {
        setResolvedValue(value);
        return;
      }
      const id = extractId(value);
      if (id) {
        const cached = itemCache.current.get(id);
        if (cached) {
          setResolvedValue(cached);
          return;
        }
      }
      // Don't clear to null while hydrating — keep previous value visible
      if (!hydrationLoading) {
        setResolvedValue(null);
      }
    }, [value, hydrationLoading, hydrationData]);

    return (
      <AsyncAutocomplete
        ref={ref}
        query={query}
        variables={{}}
        initialPageSize={25}
        additionalSearchString={defaultSearchTerm}
        getItems={(data) => {
          const searchResults = data.facilities.items;
          // Ensure the resolved value is included in options so MUI can match it
          if (resolvedValue) {
            const ids = new Set(searchResults.map((item: FacilityPickerFragment) => item.id));
            if (!ids.has(resolvedValue.id)) {
              return [...searchResults, resolvedValue];
            }
          }
          return searchResults;
        }}
        getOptionLabel={(option: FacilityPickerFragment) => {
          const name = option.name ?? "";
          return option.streetAddress1 ? `${name} (${option.streetAddress1})` : name;
        }}
        placeholderLabel={label ?? "Facility"}
        noOptionsText="Search for a facility"
        onChange={(val: FacilityPickerFragment | null) => {
          if (val) {
            itemCache.current.set(val.id, val);
          }
          setResolvedValue(val);
          onChange(val);
        }}
        value={resolvedValue}
        error={error}
        disabled={disabled || (!!idToHydrate && hydrationLoading)}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionDisabled={(option: FacilityPickerFragment) =>
          disabledFacilitiesById.includes(option?.id ?? "")
        }
        renderOption={(props, option: FacilityPickerFragment) => {
          const displayName = option.streetAddress1
            ? `${option.name} (${option.streetAddress1})`
            : option.name;
          return (
            <li {...props} key={option.id}>
              {displayName}
              {disabledOptionLabel && disabledFacilitiesById.includes(option?.id ?? "") && (
                <span>&nbsp;({disabledOptionLabel})</span>
              )}
            </li>
          );
        }}
        textFieldProps={{
          variant: "outlined",
          autoComplete: "off",
          inputProps: {
            autoComplete: "off",
            "data-lpignore": "true",
            "data-form-type": "other",
          },
          ...textFieldProps,
        }}
        lazyLoad={lazyLoad}
        hideErrors={!showErrors}
        loading={!!idToHydrate && hydrationLoading}
        onOpen={onOpen}
        onClose={onClose}
      />
    );
  }
);
